import type { ImageUpload } from "store/features/imageUploader/types";
import type { PreviewImage } from "store/types/previewImage";

export interface ColorPickerState {
    source: Source;
    image: PreviewImage;
    selectedColor: SelectedColor | null;
    currentColor: Color | null;
    fromImage: {
        colors: Color[];
    };
    isImageLoading: boolean;
    upload: ImageUpload["upload"];
}

export type Source = "color" | "image";

export const colorFormats: (keyof Color)[] = ["hex", "rgb", "hsv", "hsl", "cmyk"];
export type ColorFormat = (typeof colorFormats)[number];

export enum ColorComponentType {
    PERCENTAGE = "percentage",
    DEGREE = "degree",
    VALUE = "value",
}

export const colorComponents = {
    rgb: {
        r: {
            key: "red",
            max: 255,
            type: ColorComponentType.VALUE,
        },
        g: {
            key: "green",
            max: 255,
            type: ColorComponentType.VALUE,
        },
        b: {
            key: "blue",
            max: 255,
            type: ColorComponentType.VALUE,
        },
        a: {
            key: "opacity",
            max: 100,
            type: ColorComponentType.PERCENTAGE,
        },
    },
    hsl: {
        h: {
            key: "hue",
            max: 360,
            type: ColorComponentType.DEGREE,
        },
        s: {
            key: "saturation",
            max: 100,
            type: ColorComponentType.PERCENTAGE,
        },
        l: {
            key: "lightness",
            max: 100,
            type: ColorComponentType.PERCENTAGE,
        },
        a: {
            key: "opacity",
            max: 100,
            type: ColorComponentType.PERCENTAGE,
        },
    },
    hsv: {
        h: {
            key: "hue",
            max: 360,
            type: ColorComponentType.DEGREE,
        },
        s: {
            key: "saturation",
            max: 100,
            type: ColorComponentType.PERCENTAGE,
        },
        v: {
            key: "value",
            max: 100,
            type: ColorComponentType.PERCENTAGE,
        },
        a: {
            key: "opacity",
            max: 100,
            type: ColorComponentType.PERCENTAGE,
        },
    },
    cmyk: {
        c: {
            key: "cyan",
            max: 100,
            type: ColorComponentType.PERCENTAGE,
        },
        m: {
            key: "magenta",
            max: 100,
            type: ColorComponentType.PERCENTAGE,
        },
        y: {
            key: "yellow",
            max: 100,
            type: ColorComponentType.PERCENTAGE,
        },
        k: {
            key: "black",
            max: 100,
            type: ColorComponentType.PERCENTAGE,
        },
        a: {
            key: "opacity",
            max: 100,
            type: ColorComponentType.PERCENTAGE,
        },
    },
    hex: {},
} as const;

export interface Color {
    rgb: string;
    hex: string;
    hsv: string;
    hsl: string;
    cmyk: string;
}

interface ColorCombination {
    complementary: Color;
    monoChromatic: Color[];
    analogous: Color[];
    triadic: Color[];
    tetradic: Color[];
}

export interface SelectedColor extends Color, ColorCombination {}

export type CMYK = {
    c: number;
    m: number;
    y: number;
    k: number;
    a: number;
};

export const isCMYK = (color: unknown | CMYK): color is CMYK => {
    return (
        "c" in (color as CMYK) &&
        "m" in (color as CMYK) &&
        "y" in (color as CMYK) &&
        "k" in (color as CMYK) &&
        "a" in (color as CMYK) &&
        typeof (color as CMYK).c === "number" &&
        typeof (color as CMYK).k === "number" &&
        typeof (color as CMYK).y === "number" &&
        typeof (color as CMYK).k === "number" &&
        typeof (color as CMYK).a === "number"
    );
};
